import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import generatePrompts from 'common/src/modules/aiContent/store/aiContentProject/thunks/generatePrompts';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  itemIds: z
    .array(z.number())
    .describe('Array of item IDs to generate prompts for. Must be explicitly provided.'),
});

export default function generatePromptsTool(store: CLStore) {
  async function execute({
    input: {itemIds},
    toolUtils: {askUserConfirmation},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;
    const projectId = project?.id;

    if (!projectId) {
      return toolResult('Error: No project is currently loaded. Cannot generate prompts.');
    }

    if (!itemIds || itemIds.length === 0) {
      return toolResult(
        'Error: No item IDs provided. Please specify the item IDs to generate prompts for.',
      );
    }

    const shouldProceed = await askUserConfirmation({
      title: 'Generate Prompts',
      text: `Generate prompts for ${itemIds.length} item${itemIds.length > 1 ? 's' : ''}?`,
      okLabel: 'Generate',
      cancelLabel: 'Cancel',
    });

    if (!shouldProceed) {
      return toolResult('Prompt generation cancelled by user.');
    }

    try {
      const jobId = await store.dispatch(generatePrompts({projectId, itemIds})).unwrap();

      return toolResult(
        `Prompt generation started for ${itemIds.length} item${itemIds.length > 1 ? 's' : ''} (Job ID: ${jobId}). The items will show "Generating Prompts" status until complete.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolResult(`Error generating prompts: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Generating prompts',
    name: AiContentProjectAgentToolName.GeneratePrompts,
    description:
      'Generates AI prompts for the specified items based on the project brief and input values. This is the first step in the content generation workflow. Item IDs must be explicitly provided.',
    execute,
    inputSchema,
  });
}
